<div class="">
    <div *ngIf="isLoading" class="text-center py-10">
        <i-lucide name="loader-circle" class="animate-spin h-8 w-8 text-gray-500 mx-auto"></i-lucide>
        <p class="text-gray-600 mt-2">Loading user details...</p>
    </div>

    <div *ngIf="error" class="text-center py-10 text-red-500">
        <p>{{ error }}</p>
    </div>

    <div *ngIf="userDetails && !isLoading">
        <!-- User Profile Section -->
        <p-card header="User Profile" class="mb-6 shadow-sm rounded-lg">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
                <div><strong>Name:</strong> {{ userDetails?.user?.firstName }} {{ userDetails?.user?.lastName }}</div>
                <div><strong>Email:</strong> {{ userDetails?.user?.email }}</div>
                <div><strong>Phone:</strong> {{ userDetails?.user?.phone }}</div>
                <div><strong>Investment Goals:</strong> {{ userDetails?.user?.investmentGoals }}</div>
                <div><strong>Last Login:</strong> {{ userDetails?.user?.lastLogin | date:'medium' }}</div>
                <div><strong>Member Since:</strong> {{ userDetails?.user?.createdAt | date:'mediumDate' }}</div>
            </div>
        </p-card>

        <!-- Wallet Section -->
        <div class="mb-6 relative overflow-hidden bg-white rounded-2xl shadow-lg border border-gray-100 group hover:shadow-xl transition-all duration-300">
            <div class="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-600/5"></div>
            <div class="relative p-6">
                <div class="flex items-center justify-between mb-4">
                    <div class="flex items-center gap-3">
                        <div class="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                            <i-lucide name="wallet" class="text-white text-lg"></i-lucide>
                        </div>
                        <h3 class="text-lg font-semibold text-gray-800">Wallet Details</h3>
                    </div>
                    <div *ngIf="userDetails?.wallet?.isLocked" class="inline-flex items-center text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full">
                        <i-lucide name="lock" class="mr-1"></i-lucide>
                        Locked
                    </div>
                </div>

                <div *ngIf="userDetails.wallet">
                    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                        <!-- Total Balance -->
                        <div class="bg-blue-50 rounded-xl p-4 border border-blue-100">
                            <p class="text-sm font-medium text-blue-700 mb-1">Total Balance</p>
                            <p class="text-3xl font-bold text-blue-600">{{ formatCurrency(userDetails.wallet.balance) }}</p>
                        </div>

                        <!-- On hold -->
                        <div class="bg-amber-50 rounded-xl p-4 border border-amber-100">
                            <p class="text-xs font-medium text-amber-700 mb-1">On hold</p>
                            <p class="text-lg font-bold text-amber-600">{{ formatCurrency(userDetails.wallet.pendingWithdrawals) }}</p>
                        </div>

                        <!-- Available to withdraw -->
                        <div class="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                            <p class="text-xs font-medium text-emerald-700 mb-1">Available to withdraw</p>
                            <p class="text-lg font-bold text-emerald-600">{{ formatCurrency(userDetails.wallet.spendableBalance) }}</p>
                        </div>
                    </div>

                    <!-- Info Note -->
                    <!-- <div class="bg-gray-50 rounded-xl p-3 mb-4">
                        <p class="text-xs text-gray-600 flex items-center gap-1">
                            <i-lucide name="info" class="mr-1"></i-lucide>
                            ROI is prioritized during withdrawals
                        </p>
                    </div> -->
                </div>
                <div *ngIf="!userDetails.wallet" class="text-gray-500 p-6">No wallet details available.</div>
            </div>
        </div>

        <!-- Investments Section -->
        <p-card header="Investments" class="mb-6 shadow-sm rounded-lg">
            <div *ngIf="userDetails.investments && userDetails.investments.length > 0">
                <p-table [value]="userDetails.investments" [paginator]="false" [rows]="5" [responsive]="true" styleClass="p-datatable-sm p-datatable-striped">
                    <ng-template pTemplate="header">
                        <tr>
                            <th class="text-gray-600 font-medium">Portfolio</th>
                            <th class="text-gray-600 font-medium">Amount Invested</th>
                            <th class="text-gray-600 font-medium">Current Value</th>
                            <th class="text-gray-600 font-medium">ROI %</th>
                            <th class="text-gray-600 font-medium">Status</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-investment>
                        <tr class="hover:bg-gray-50">
                            <td class="text-gray-700">{{ investment.portfolioId?.name || 'N/A' }}</td>
                            <td class="text-gray-700">{{ formatCurrency(investment.totalInvestedAmount) }}</td>
                            <td class="text-gray-700">{{ formatCurrency(investment.currentValue) }}</td>
                            <td class="text-gray-700">{{ investment.actualRoiPercentage | number:'1.2-2' }}</td>
                            <td><p-tag [value]="investment.status" [severity]="getStatusClass(investment.status)"></p-tag></td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr>
                            <td colspan="5" class="text-center text-gray-500 py-8">
                                <div class="flex flex-col items-center gap-3">
                                    <i-lucide name="inbox" class="text-4xl text-gray-300"></i-lucide>
                                    <p>No investments found.</p>
                                </div>
                            </td>
                        </tr>
                    </ng-template>
                </p-table>
            </div>
            <div *ngIf="!userDetails.investments || userDetails.investments.length === 0" class="text-gray-500 py-8 text-center">
                <div class="flex flex-col items-center gap-3">
                    <i-lucide name="inbox" class="text-4xl text-gray-300"></i-lucide>
                    <p>No investments found.</p>
                </div>
            </div>
        </p-card>

        <!-- Transactions Section -->
        <p-card header="Transactions" class="mb-6 shadow-sm rounded-lg">
            <div *ngIf="userDetails.transactions && userDetails.transactions.length > 0">
                <p-table [value]="userDetails.transactions" [paginator]="false" [rows]="5" [responsive]="true" styleClass="p-datatable-sm p-datatable-striped">
                    <ng-template pTemplate="header">
                        <tr>
                            <!-- <th class="text-gray-600 font-medium">Type</th> -->
                            <th class="text-gray-600 font-medium">Amount</th>
                            <th class="text-gray-600 font-medium">Status</th>
                            <th class="text-gray-600 font-medium">Date</th>
                            <th class="text-gray-600 font-medium">Portfolio</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-transaction>
                        <tr class="hover:bg-gray-50">
                            <!-- <td class="text-gray-700">{{ transaction.transactionType }}</td> -->
                            <td class="text-gray-700">{{ formatCurrency(transaction.amount) }}</td>
                            <td>
                                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" [ngClass]="getStatusClass(transaction.status)">
                                    <i-lucide [name]="getStatusIcon(transaction.status)" class="h-3 w-3 mr-1"></i-lucide>
                                    {{ transaction.status }}
                                </span>
                            </td>
                            <td class="text-gray-700">{{ transaction.createdAt | date:'medium' }}</td>
                            <td class="text-gray-700">{{ transaction.portfolioId?.name || 'N/A' }}</td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr>
                            <td colspan="5" class="text-center text-gray-500 py-8">
                                <div class="flex flex-col items-center gap-3">
                                    <i-lucide name="inbox" class="text-4xl text-gray-300"></i-lucide>
                                    <p>No transactions found.</p>
                                </div>
                            </td>
                        </tr>
                    </ng-template>
                </p-table>
            </div>
            <div *ngIf="!userDetails.transactions || userDetails.transactions.length === 0" class="text-gray-500 py-8 text-center">
                <div class="flex flex-col items-center gap-3">
                    <i-lucide name="inbox" class="text-4xl text-gray-300"></i-lucide>
                    <p>No transactions found.</p>
                </div>
            </div>
        </p-card>

        <!-- Withdrawal Requests Section -->
        <p-card header="Withdrawal Requests" class="mb-6 shadow-sm rounded-lg">
            <div *ngIf="userDetails.withdrawals && userDetails.withdrawals.length > 0">
                <p-table [value]="userDetails.withdrawals" [paginator]="false" [rows]="5" [responsive]="true" styleClass="p-datatable-sm p-datatable-striped">
                    <ng-template pTemplate="header">
                        <tr>
                            <th class="text-gray-600 font-medium">Date</th>
                            <th class="text-gray-600 font-medium text-right">Amount</th>
                            <th class="text-gray-600 font-medium">Status</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-withdrawal>
                        <tr class="hover:bg-gray-50">
                            <td class="text-gray-700">{{ withdrawal.createdAt | date:'medium' }}</td>
                            <td class="text-right text-gray-700">{{ formatCurrency(withdrawal.amount) }}</td>
                            <td>
                                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" [ngClass]="getStatusClass(withdrawal.status)">
                                    <i-lucide [name]="getStatusIcon(withdrawal.status)" class="h-3 w-3 mr-1"></i-lucide>
                                    {{ withdrawal.status }}
                                </span>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr>
                            <td colspan="3" class="text-center text-gray-500 py-8">
                                <div class="flex flex-col items-center gap-3">
                                    <i-lucide name="inbox" class="text-4xl text-gray-300"></i-lucide>
                                    <p>No withdrawal requests found.</p>
                                </div>
                            </td>
                        </tr>
                    </ng-template>
                </p-table>
            </div>
            <div *ngIf="!userDetails.withdrawals || userDetails.withdrawals.length === 0" class="text-gray-500 py-8 text-center">
                <div class="flex flex-col items-center gap-3">
                    <i-lucide name="inbox" class="text-4xl text-gray-300"></i-lucide>
                    <p>No withdrawal requests found.</p>
                </div>
            </div>
        </p-card>
    </div>
</div>
